import { useMemo } from 'react';

/**
 * Tiny SVG sketch of a route — no map tiles, just the geometry projected into a
 * viewBox. Downsamples to ~60 points so a list of these stays cheap to render.
 * Falls back to waypoints when no geometry is present.
 */
export function RouteThumbnail({
  geometry, waypoints, width = 72, height = 56,
  stroke = 'var(--color-primary)', className = '',
}) {
  const path = useMemo(() => {
    let coords = geometry?.coordinates;
    if ((!coords || coords.length < 2) && waypoints?.length >= 2) {
      coords = waypoints.filter(w => w.lat != null && w.lng != null).map(w => [w.lng, w.lat]);
    }
    if (!coords || coords.length < 2) return null;

    // Downsample for performance.
    const MAX = 60;
    const step = Math.max(1, Math.floor(coords.length / MAX));
    const pts = coords.filter((_, i) => i % step === 0);
    const last = coords[coords.length - 1];
    if (pts[pts.length - 1] !== last) pts.push(last);

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of pts) {
      if (x < minX) minX = x; if (x > maxX) maxX = x;
      if (y < minY) minY = y; if (y > maxY) maxY = y;
    }
    const pad = 6;
    const w = maxX - minX || 1e-6;
    const h = maxY - minY || 1e-6;
    const scale = Math.min((width - pad * 2) / w, (height - pad * 2) / h);
    const ox = (width - w * scale) / 2;
    const oy = (height - h * scale) / 2;
    const px = (x) => ox + (x - minX) * scale;
    const py = (y) => oy + (maxY - y) * scale; // flip lat → screen y

    const d = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${px(x).toFixed(1)} ${py(y).toFixed(1)}`).join(' ');
    const s = pts[0], e = pts[pts.length - 1];
    return { d, sx: px(s[0]), sy: py(s[1]), ex: px(e[0]), ey: py(e[1]) };
  }, [geometry, waypoints, width, height]);

  if (!path) return null;
  return (
    <svg className={`route-thumb ${className}`} width={width} height={height}
      viewBox={`0 0 ${width} ${height}`} fill="none" aria-hidden="true">
      <path d={path.d} stroke={stroke} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={path.sx} cy={path.sy} r="2.6" fill="#22c55e" />
      <circle cx={path.ex} cy={path.ey} r="2.6" fill="#ef4444" />
    </svg>
  );
}
